import React, { PropsWithChildren, useContext } from 'react'
import { View, Text, StyleSheet, FlatList } from 'react-native'

import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COMMON_STYLES,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { DIMENSION_WIDTH } from 'app/utils/common'

interface SectionLayoutProps {
  title: string
  description: string
  loading?: boolean
  onViewAllClick?: () => void
  isViewAllCTADisabled?: boolean
  highlight: boolean
}

const SectionLayout = ({
  title,
  description,
  loading,
  children,
  onViewAllClick,
  isViewAllCTADisabled,
  highlight,
}: PropsWithChildren<SectionLayoutProps>) => {
  const { mediaStyles } = useContext(ResponsiveContext)

  return (
    <View
      style={[
        COMMON_STYLES.pv2,
        BG_STYLES.bgBlue10,
        mediaStyles({
          desktopStyle: [
            styles['br5/4'],
            COMMON_STYLES.row,
            COMMON_STYLES.ph2,
            COMMON_STYLES.mv1,
          ],
          mobileStyle: [COMMON_STYLES['ph3/2'], COMMON_STYLES['mv1/2']],
        }),
        highlight && [COMMON_STYLES.b1, BORDER_STYLES.borderPrimary100],
      ]}
    >
      <View
        style={mediaStyles({
          desktopStyle: [COMMON_STYLES.mr1, { width: 227 }],
          mobileStyle: COMMON_STYLES.row,
        })}
      >
        <View
          style={mediaStyles({ mobileStyle: { width: DIMENSION_WIDTH - 160 } })}
        >
          <Text
            style={[
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral100,
              COMMON_STYLES['mb1/4'],
              mediaStyles({
                desktopStyle: TYPOGRAPHY.HEADING1,
                mobileStyle: TYPOGRAPHY.HEADING3,
              }),
            ]}
          >
            {title}
          </Text>
          <Text
            style={[
              VARIANTS_STYLES.MEDIUM,
              TEXT_STYLES.textNeutral80,
              mediaStyles({
                desktopStyle: TYPOGRAPHY.TEXT1,
                mobileStyle: TYPOGRAPHY.TEXT2,
              }),
            ]}
          >
            {description}
          </Text>
        </View>

        {onViewAllClick ? (
          <View
            style={mediaStyles({
              desktopStyle: [COMMON_STYLES.justifyCenter, COMMON_STYLES.mt1],
              mobileStyle: [COMMON_STYLES.alignItemsCenter, COMMON_STYLES.mh1],
            })}
          >
            <Button
              onClick={() => !isViewAllCTADisabled && onViewAllClick()}
              text={'View All'}
              buttonStyle={styles.w100}
              type={BUTTON_TYPE.SECONDARY}
              status={
                isViewAllCTADisabled
                  ? BUTTON_STATUS.DISABLED
                  : BUTTON_STATUS.ACTIVE
              }
            />
          </View>
        ) : null}
      </View>
      {loading ? (
        <Spinner />
      ) : (
        <FlatList
          horizontal
          data={React.Children.toArray(children)}
          renderItem={({ item }) => <>{item}</>}
          keyExtractor={(_, index) => index.toString()}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[
            mediaStyles({
              desktopStyle: styles.gap1,
              mobileStyle: [COMMON_STYLES.mt1, styles['gap1/2']],
            }),
          ]}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  gap1: { gap: 16 },
  'gap1/2': { gap: 8 },
  'br5/4': { borderRadius: 24 },
  w100: { width: 100 },
})

export default SectionLayout
